import { useEffect, useMemo, useRef, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Activity, ImagePlus, Menu, Search, Send, ShieldCheck, X } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { ACTIVE_GAME_CATALOG } from "../data/gameCatalog";
import { apiUrl } from "./api";

export const STATUS_COLORS = {
  undetected: "text-green-400",
  updating: "text-yellow-400",
  detected: "text-red-400",
  frozen: "text-gray-400",
};

export const STATUS_ORDER = ["undetected", "updating", "detected", "frozen"];
export const STATUS_FILTERS = ["all", ...STATUS_ORDER];

export function getStatusIndex(status) {
  const index = STATUS_ORDER.indexOf(status);
  return index === -1 ? Number.MAX_SAFE_INTEGER : index;
}

export function formatOs(os) {
  return Array.isArray(os) ? os.join(", ") : os || "N/A";
}

export function dataUrl(dataFile) {
  return `/data/${dataFile}?v=${Date.now()}`;
}

const prefetchedDataFiles = new Set();
const preloadedImages = new Set();

export function prefetchDataFile(dataFile) {
  if (!dataFile || prefetchedDataFiles.has(dataFile)) return;
  prefetchedDataFiles.add(dataFile);
  fetch(dataUrl(dataFile), { cache: "no-store" }).catch(() => {
    prefetchedDataFiles.delete(dataFile);
  });
}

export function preloadImage(src) {
  if (!src || preloadedImages.has(src) || typeof window === "undefined") return;
  preloadedImages.add(src);
  const image = new Image();
  image.decoding = "async";
  image.src = src;
}

export function formatDateTime(value) {
  if (!value) return "Not recorded yet";
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return "Not recorded yet";
  return new Date(timestamp).toLocaleString();
}

export function statusLabel(status) {
  return String(status || "unknown").charAt(0).toUpperCase() + String(status || "unknown").slice(1);
}

export function getAvailabilityMessage(status) {
  if (status === "undetected") return "Available for purchase";
  if (status === "updating") return "Updating - contact support before purchase";
  if (status === "detected") return "Detected - purchase not recommended";
  if (status === "frozen") return "Frozen - purchase paused";
  return "Check status before purchase";
}

export function ProductInfoBadges({ item, compact = false, availabilityOnly = false }) {
  const osList = Array.isArray(item?.os) ? item.os : [];
  const badgeClass = compact
    ? "rounded-md border border-purple-400/20 bg-black/25 px-2 py-1 text-xs font-bold text-purple-100"
    : "rounded-lg border border-purple-400/20 bg-black/25 px-3 py-1.5 text-sm font-bold text-purple-100";
  const readyClass = compact
    ? "status-pulse status-undetected rounded-md border border-green-400/25 bg-green-500/10 px-2 py-1 text-xs font-bold text-green-200"
    : "status-pulse status-undetected rounded-lg border border-green-400/25 bg-green-500/10 px-3 py-1.5 text-sm font-bold text-green-200";
  const warningClass = compact
    ? `status-pulse status-${item?.status || "unknown"} rounded-md border border-amber-400/25 bg-amber-500/10 px-2 py-1 text-xs font-bold text-amber-200`
    : `status-pulse status-${item?.status || "unknown"} rounded-lg border border-amber-400/25 bg-amber-500/10 px-3 py-1.5 text-sm font-bold text-amber-200`;

  return (
    <div className="flex flex-wrap gap-2">
      {!availabilityOnly && <span className={badgeClass}>Live status</span>}
      {item?.status === "undetected" ? (
        <span className={readyClass}>Ready</span>
      ) : (
        <span className={warningClass}>Check before buying</span>
      )}
      {!availabilityOnly && osList.length > 0 && <span className={badgeClass}>Win {osList.join("/")}</span>}
      {!availabilityOnly && item?.price && <span className={badgeClass}>{item.price}</span>}
    </div>
  );
}

export function SeoMeta({ title, description, image = "/preview.png", imageAlt = "HackSeller", canonicalPath = "" }) {
  useEffect(() => {
    const siteName = "HackSeller";
    const nextTitle = title ? `${title} | ${siteName}` : "HackSeller | Private gaming tools";
    const nextDescription = description || "Live catalog, product status, delivery and support information.";
    const origin = window.location.origin;
    const canonical = `${origin}${canonicalPath || window.location.pathname}`;
    const previousTitle = document.title;

    const upsert = (selector, attributes) => {
      let element = document.head.querySelector(selector);
      if (!element) {
        element = document.createElement(attributes.property ? "meta" : "meta");
        document.head.appendChild(element);
      }
      Object.entries(attributes).forEach(([key, value]) => element.setAttribute(key, value));
      return element;
    };

    const canonicalElement = document.head.querySelector('link[rel="canonical"]') || document.createElement("link");
    canonicalElement.setAttribute("rel", "canonical");
    canonicalElement.setAttribute("href", canonical);
    if (!canonicalElement.parentNode) document.head.appendChild(canonicalElement);

    document.title = nextTitle;
    upsert('meta[name="description"]', { name: "description", content: nextDescription });
    upsert('meta[property="og:title"]', { property: "og:title", content: nextTitle });
    upsert('meta[property="og:description"]', { property: "og:description", content: nextDescription });
    upsert('meta[property="og:url"]', { property: "og:url", content: canonical });
    upsert('meta[property="og:image"]', { property: "og:image", content: image.startsWith("http") ? image : `${origin}${image}` });
    upsert('meta[property="og:image:alt"]', { property: "og:image:alt", content: imageAlt });
    upsert('meta[property="og:site_name"]', { property: "og:site_name", content: siteName });
    upsert('meta[name="twitter:title"]', { name: "twitter:title", content: nextTitle });
    upsert('meta[name="twitter:description"]', { name: "twitter:description", content: nextDescription });
    upsert('meta[name="twitter:image"]', { name: "twitter:image", content: image.startsWith("http") ? image : `${origin}${image}` });
    upsert('meta[name="twitter:image:alt"]', { name: "twitter:image:alt", content: imageAlt });

    return () => {
      document.title = previousTitle;
    };
  }, [canonicalPath, description, image, imageAlt, title]);

  return null;
}

export function FallbackImage({ src, alt, className = "", fallbackClassName = "", label }) {
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    setFailed(false);
  }, [src]);

  if (!src || failed) {
    const initials = String(label || alt || "?")
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0])
      .join("")
      .toUpperCase();

    return (
      <div
        className={`inline-flex items-center justify-center bg-gray-950 text-purple-200 border border-purple-400/25 ${className} ${fallbackClassName}`}
        title={alt}
      >
        <span className="text-center text-[0.62em] font-extrabold leading-none">{initials || "HS"}</span>
      </div>
    );
  }

  return <img src={src} alt={alt} className={className} onError={() => setFailed(true)} loading="lazy" />;
}

async function chatRequest(path, options = {}) {
  const response = await fetch(apiUrl(path), {
    credentials: "include",
    headers: {
      "Content-Type": "application/json",
      ...(options.headers || {}),
    },
    ...options,
  });
  const data = await response.json().catch(() => ({}));
  if (!response.ok) {
    throw new Error(data.error || `HTTP ${response.status}`);
  }
  return data;
}

function LinkifiedText({ text }) {
  const parts = String(text || "").split(/(https?:\/\/[^\s<>"']+)/gi);

  return (
    <>
      {parts.map((part, index) => {
        if (!/^https?:\/\//i.test(part)) return <span key={`${part}-${index}`}>{part}</span>;

        return (
          <a
            key={`${part}-${index}`}
            href={part}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-purple-200 underline decoration-purple-300/60 underline-offset-2 hover:text-purple-100"
          >
            {part}
          </a>
        );
      })}
    </>
  );
}

function PixelChatIcon({ isOpen }) {
  return (
    <span className={`pixel-chat-logo ${isOpen ? "pixel-chat-logo-open" : ""}`} aria-hidden="true">
      <span className="pixel-chat-logo-bubble" />
      <span className="pixel-chat-logo-face">
        <span className="pixel-chat-logo-scanline" />
        <span className="pixel-chat-logo-eye pixel-chat-logo-eye-left" />
        <span className="pixel-chat-logo-eye pixel-chat-logo-eye-right" />
        <span className="pixel-chat-logo-mouth" />
      </span>
      <span className="pixel-chat-logo-tail" />
      <span className="pixel-chat-logo-pixel pixel-chat-logo-pixel-one" />
      <span className="pixel-chat-logo-pixel pixel-chat-logo-pixel-two" />
    </span>
  );
}

function getLisbonSupportProgress() {
  const previewProgress = new URLSearchParams(window.location.search).get("chatSupportProgress");
  if (previewProgress !== null) {
    const progress = Number(previewProgress);
    if (Number.isFinite(progress)) return Math.max(0, Math.min(100, progress));
  }

  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Lisbon",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .formatToParts(new Date())
    .reduce((accumulator, part) => {
      accumulator[part.type] = part.value;
      return accumulator;
    }, {});

  const hour = Number(parts.hour);
  const minute = Number(parts.minute);
  const second = Number(parts.second);
  const currentSeconds = hour * 3600 + minute * 60 + second;
  const openSeconds = 9 * 3600;
  const closeSeconds = 23 * 3600;

  if (currentSeconds < openSeconds || currentSeconds >= closeSeconds) return 0;

  const remainingRatio = (closeSeconds - currentSeconds) / (closeSeconds - openSeconds);
  return Math.max(0, Math.min(100, remainingRatio * 100));
}

function getLisbonTimeZoneLabel() {
  const timeZoneName = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Lisbon",
    timeZoneName: "shortOffset",
  })
    .formatToParts(new Date())
    .find((part) => part.type === "timeZoneName")?.value;

  return timeZoneName?.replace("GMT", "UTC") || "UTC";
}

function getLisbonSupportStatus() {
  const parts = new Intl.DateTimeFormat("en-GB", {
    timeZone: "Europe/Lisbon",
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    hour12: false,
  })
    .formatToParts(new Date())
    .reduce((accumulator, part) => {
      accumulator[part.type] = part.value;
      return accumulator;
    }, {});

  const hour = Number(parts.hour);
  const minute = Number(parts.minute);
  const second = Number(parts.second);
  const currentSeconds = hour * 3600 + minute * 60 + second;
  const openSeconds = 9 * 3600;
  const closeSeconds = 23 * 3600;
  const timeZoneLabel = getLisbonTimeZoneLabel();

  if (currentSeconds >= openSeconds && currentSeconds < closeSeconds) {
    const remainingSeconds = closeSeconds - currentSeconds;
    const remainingHours = Math.floor(remainingSeconds / 3600);
    const remainingMinutes = Math.ceil((remainingSeconds % 3600) / 60);
    const hourLabel = remainingHours > 0 ? `${remainingHours}h` : "";
    const minuteLabel = remainingMinutes > 0 ? `${remainingMinutes}min` : "";

    return {
      schedule: `Support 09:00-23:00 (${timeZoneLabel})`,
      state: `${[hourLabel, minuteLabel].filter(Boolean).join(" ")} left`,
    };
  }

  return {
    schedule: `Support 09:00-23:00 (${timeZoneLabel})`,
    state: "currently closed",
  };
}

function ChatAttachment({ attachment }) {
  if (!attachment?.dataUrl) return null;

  return (
    <a href={attachment.dataUrl} target="_blank" rel="noopener noreferrer" className="mt-2 block">
      <img
        src={attachment.dataUrl}
        alt={attachment.name || "Chat attachment"}
        className="max-h-44 w-full rounded-md border border-white/15 object-contain"
      />
    </a>
  );
}

function playChatSound() {
  try {
    const AudioContext = window.AudioContext || window.webkitAudioContext;
    if (!AudioContext) return;

    const context = new AudioContext();
    const gain = context.createGain();
    gain.gain.setValueAtTime(0.0001, context.currentTime);
    gain.gain.exponentialRampToValueAtTime(0.08, context.currentTime + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.0001, context.currentTime + 0.24);
    gain.connect(context.destination);

    [523.25, 659.25].forEach((frequency, index) => {
      const oscillator = context.createOscillator();
      oscillator.type = "square";
      oscillator.frequency.setValueAtTime(frequency, context.currentTime + index * 0.07);
      oscillator.connect(gain);
      oscillator.start(context.currentTime + index * 0.07);
      oscillator.stop(context.currentTime + index * 0.07 + 0.11);
    });

    setTimeout(() => context.close().catch(() => null), 420);
  } catch {
    // Browsers can block audio before user interaction; the visual alert still works.
  }
}

function imageFileToAttachment(file) {
  return new Promise((resolve, reject) => {
    const allowedTypes = ["image/png", "image/jpeg", "image/webp", "image/gif"];
    if (!allowedTypes.includes(file.type)) {
      reject(new Error("Only PNG, JPG, WEBP, and GIF images are supported."));
      return;
    }
    if (file.size > 650 * 1024) {
      reject(new Error("Image must be under 650 KB."));
      return;
    }

    const reader = new FileReader();
    reader.onload = () => resolve({ name: file.name, type: file.type, dataUrl: reader.result });
    reader.onerror = () => reject(new Error("Image could not be read."));
    reader.readAsDataURL(file);
  });
}

export function LiveChatWidget() {
  const location = useLocation();
  const [isOpen, setIsOpen] = useState(false);
  const [session, setSession] = useState(null);
  const [name, setName] = useState(() => localStorage.getItem("hs_chat_name") || "");
  const [draft, setDraft] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [attention, setAttention] = useState(false);
  const [error, setError] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [supportProgress, setSupportProgress] = useState(getLisbonSupportProgress);
  const [supportStatus, setSupportStatus] = useState(getLisbonSupportStatus);
  const fileInputRef = useRef(null);
  const typingTimeoutRef = useRef(null);
  const lastTypingSentRef = useRef(0);
  const previousUnreadRef = useRef(null);

  useEffect(() => {
    window.__HS_DISABLE_TAWK__ = true;

    const removeTawk = () => {
      document
        .querySelectorAll('script[src*="embed.tawk.to"], iframe[src*="tawk.to"], iframe[src*="tawk.link"], #tawkchat-minified-wrapper, .tawk-min-container')
        .forEach((node) => node.remove());
      delete window.Tawk_API;
      delete window.Tawk_LoadStart;
    };

    removeTawk();
    const observer = new MutationObserver(removeTawk);
    observer.observe(document.body, { childList: true, subtree: true });
    const intervalId = setInterval(removeTawk, 1000);
    return () => {
      observer.disconnect();
      clearInterval(intervalId);
    };
  }, []);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setSupportProgress(getLisbonSupportProgress());
      setSupportStatus(getLisbonSupportStatus());
    }, 30000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const sessionId = session?.id || localStorage.getItem("hs_chat_session");
    if (!sessionId) return undefined;

    let active = true;
    const loadSession = async () => {
      try {
        const data = await chatRequest(`/api/chat/${sessionId}${isOpen ? "?read=1" : ""}`);
        if (active) setSession(data.session);
      } catch {
        localStorage.removeItem("hs_chat_session");
      }
    };

    loadSession();
    const intervalId = setInterval(loadSession, isOpen ? 2500 : 7000);
    return () => {
      active = false;
      clearInterval(intervalId);
    };
  }, [isOpen, session?.id]);

  useEffect(() => () => {
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }
  }, []);

  useEffect(() => {
    const unread = session?.customerUnread || 0;
    const previousUnread = previousUnreadRef.current;
    previousUnreadRef.current = unread;

    if (!isOpen && previousUnread !== null && unread > previousUnread) {
      setAttention(true);
      playChatSound();
    }
    if (isOpen || unread === 0) {
      setAttention(false);
    }
  }, [isOpen, session?.customerUnread]);

  if (location.pathname === "/ops-vault-a91f6d" || window.self !== window.top) {
    return null;
  }

  const messages = session?.messages || [];
  const unread = session?.customerUnread || 0;
  const latestStaffMessage = [...messages].reverse().find((message) => message.from === "staff");
  const supportIsOpen = supportProgress > 0;

  const startChatSession = async (message, nextAttachment = null) => {
    const data = await chatRequest("/api/chat/start", {
      method: "POST",
      body: JSON.stringify({
        name: name.trim() || "Visitor",
        message,
        ...(nextAttachment ? { attachment: nextAttachment } : {}),
        page: `${location.pathname}${location.search}`,
      }),
    });
    if (!data.session?.id) {
      throw new Error("Chat API did not return a valid session.");
    }
    setSession(data.session);
    localStorage.setItem("hs_chat_session", data.session.id);
    localStorage.setItem("hs_chat_name", name.trim() || "Visitor");
    return data.session;
  };

  const sendTyping = async (typing) => {
    if (!session?.id) return;
    try {
      await chatRequest(`/api/chat/${session.id}/typing`, {
        method: "POST",
        body: JSON.stringify({ typing }),
      });
    } catch {
      // Typing presence is best effort; message sending remains the important path.
    }
  };

  const handleDraftChange = (event) => {
    const value = event.target.value;
    setDraft(value);
    if (!session?.id) return;

    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    if (value.trim()) {
      const now = Date.now();
      if (now - lastTypingSentRef.current > 1200) {
        lastTypingSentRef.current = now;
        sendTyping(true);
      }
      typingTimeoutRef.current = setTimeout(() => sendTyping(false), 1800);
    } else {
      sendTyping(false);
    }
  };

  const submit = async (event) => {
    event.preventDefault();
    const message = draft.trim();
    if (!message && !attachment) return;

    setIsSending(true);
    setError("");

    try {
      if (typingTimeoutRef.current) {
        clearTimeout(typingTimeoutRef.current);
      }
      await sendTyping(false);

      if (session?.id) {
        try {
          const data = await chatRequest(`/api/chat/${session.id}/message`, {
            method: "POST",
            body: JSON.stringify({
              message,
              ...(attachment ? { attachment } : {}),
              page: `${location.pathname}${location.search}`,
            }),
          });
          setSession(data.session);
        } catch (sendError) {
          if (!/Conversation not found/i.test(sendError.message)) throw sendError;
          throw new Error("This conversation is no longer available. Please contact support from your existing conversation history.");
        }
      } else {
        await startChatSession(message, attachment);
      }
      setDraft("");
      setAttachment(null);
    } catch (chatError) {
      setError(chatError.message);
    } finally {
      setIsSending(false);
    }
  };

  const handleImageChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setError("");
    try {
      setAttachment(await imageFileToAttachment(file));
    } catch (imageError) {
      setError(imageError.message);
    }
  };

  return (
    <div className="pointer-events-none fixed inset-0 z-[80] font-jersey text-white">
      <AnimatePresence>
        {isOpen && (
        <motion.section
          initial={{ opacity: 0, y: 18, scale: 0.92, filter: "brightness(1.8)" }}
          animate={{ opacity: 1, y: 0, scale: 1, filter: "brightness(1)" }}
          exit={{ opacity: 0, y: 18, scale: 0.9, filter: "brightness(1.6)" }}
          transition={{ duration: 0.18, ease: "easeOut" }}
          style={{
            position: "fixed",
            right: "clamp(0.75rem, 4vw, 1.25rem)",
            bottom: "5.75rem",
            width: "min(calc(100vw - 1.5rem), 400px)",
          }}
          className="live-chat-panel pointer-events-auto flex h-[min(520px,calc(100vh-7rem))] flex-col overflow-hidden rounded-lg border border-purple-400/30 bg-gray-950/95 shadow-2xl shadow-black/50 backdrop-blur-xl"
        >
          <header className="border-b border-purple-400/20 bg-purple-600/15 px-4 py-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <p className="live-chat-neon-title text-xl font-extrabold leading-none">Live support</p>
                  <span className={`rounded border px-2 py-0.5 text-xs font-extrabold uppercase ${
                    supportIsOpen
                      ? "border-green-400/35 bg-green-500/15 text-green-100"
                      : "border-gray-400/25 bg-gray-500/10 text-gray-200"
                  }`}>
                    {supportIsOpen ? "Online" : "Closed"}
                  </span>
                </div>
                {session?.status === "closed" && <p className="mt-1 text-sm text-purple-100">Conversation closed</p>}
                <p className="mt-1 text-base leading-tight text-purple-200/90">{supportStatus.schedule}</p>
                <p className="text-base leading-tight text-purple-200/90">{supportStatus.state}</p>
              </div>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded-lg border border-purple-400/25 bg-black/30 p-2 text-purple-100 transition hover:bg-purple-500/15"
                aria-label="Close chat"
              >
                <X size={18} />
              </button>
            </div>

            <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-black/45">
              <span
                className={`block h-full rounded-full ${supportIsOpen ? "bg-green-400" : "bg-gray-500"}`}
                style={{ width: `${Math.max(8, supportProgress)}%` }}
              />
            </div>
          </header>

          <div className="flex-1 space-y-3 overflow-y-auto px-4 py-4">
            {messages.length === 0 && (
              <div className="rounded-lg border border-purple-400/15 bg-black/25 p-3 text-base text-gray-300">
                <p className="font-extrabold text-purple-100">Need help?</p>
                <p className="mt-1 leading-snug">Ask about a product, payment, setup, or delivery.</p>
              </div>
            )}
            {messages.map((message) => (
              <div key={message.id} className={`flex ${message.from === "customer" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[82%] rounded-lg border px-3 py-2 text-base ${
                  message.from === "customer"
                    ? "border-purple-400/30 bg-purple-600/25 text-purple-50"
                    : "border-green-400/25 bg-green-500/15 text-green-50"
                }`}>
                  {message.text && <p className="whitespace-pre-wrap break-words"><LinkifiedText text={message.text} /></p>}
                  <ChatAttachment attachment={message.attachment} />
                  <p className="mt-1 text-xs opacity-60">
                    {new Date(message.at).toLocaleTimeString()}
                    {message.from === "customer" && (
                      <span className="ml-2">{message.seenByStaffAt ? "Seen" : "Sent"}</span>
                    )}
                  </p>
                </div>
              </div>
            ))}
            {session?.staffTyping && (
              <div className="flex justify-start">
                <div className="rounded-lg border border-green-400/20 bg-green-500/10 px-3 py-2 text-sm text-green-100">
                  Support is typing...
                </div>
              </div>
            )}
          </div>

          <form onSubmit={submit} className="border-t border-purple-400/20 bg-black/15 p-3">
            {!session && (
              <input
                value={name}
                onChange={(event) => setName(event.target.value)}
                placeholder="Your name"
                className="admin-field mb-2 w-full rounded-lg border border-purple-400/20 bg-black/40 px-3 py-2 text-base text-white outline-none focus:ring-2 focus:ring-purple-400"
              />
            )}
            <div className="flex gap-2">
              <input
                value={draft}
                onChange={handleDraftChange}
                placeholder="Type your message..."
                maxLength={1000}
                className="admin-field min-w-0 flex-1 rounded-lg border border-purple-400/20 bg-black/40 px-3 py-2 text-base text-white outline-none focus:ring-2 focus:ring-purple-400"
              />
              <input ref={fileInputRef} type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={handleImageChange} className="hidden" />
              <button
                type="button"
                onClick={() => fileInputRef.current?.click()}
                className="inline-flex items-center justify-center rounded-lg border border-purple-400/30 bg-black/35 px-3 py-2 text-purple-100 transition hover:bg-purple-500/15"
                aria-label="Attach image"
              >
                <ImagePlus size={18} />
              </button>
              <button
                type="submit"
                disabled={isSending || (!draft.trim() && !attachment)}
                className="inline-flex items-center justify-center rounded-lg border border-purple-400/30 bg-purple-600 px-3 py-2 text-white transition hover:bg-purple-500 disabled:cursor-not-allowed disabled:opacity-45"
                aria-label="Send message"
              >
                <Send size={18} />
              </button>
            </div>
            {attachment && (
              <div className="mt-2 flex items-center justify-between gap-2 rounded-lg border border-purple-400/20 bg-black/30 px-3 py-2 text-sm text-purple-100">
                <span className="truncate">{attachment.name}</span>
                <button type="button" onClick={() => setAttachment(null)} className="text-purple-200 hover:text-white">
                  Remove
                </button>
              </div>
            )}
            {error && <p className="mt-2 text-sm text-red-200">{error}</p>}
          </form>
        </motion.section>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {!isOpen && attention && latestStaffMessage && (
          <motion.div
            initial={{ opacity: 0, y: 8, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 8, scale: 0.95 }}
            style={{
              position: "fixed",
              right: "clamp(1rem, 4vw, 1.25rem)",
              bottom: "6rem",
              width: "min(82vw, 280px)",
            }}
            className="live-chat-reply-popover pointer-events-auto rounded-lg border border-purple-300/35 bg-gray-950/95 px-3 py-2 text-sm text-purple-50 shadow-xl shadow-black/40"
          >
            <p className="font-bold text-purple-200">New reply</p>
            <p className="truncate text-gray-200">
              {latestStaffMessage.text || latestStaffMessage.attachment?.name || "Image attached"}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      <motion.button
        type="button"
        onClick={() => setIsOpen((prev) => !prev)}
        className={`live-chat-float-button pointer-events-auto fixed bottom-5 right-5 inline-flex h-16 items-center justify-center gap-2 px-3 text-white transition-colors ${isOpen ? "live-chat-float-button-open" : ""} ${attention ? "live-chat-float-button-attention" : ""}`}
        style={{ "--live-chat-support-progress": `${supportProgress}%` }}
        aria-label={isOpen ? "Close live chat" : "Open live chat"}
        animate={{
          rotate: 0,
          scale: 1,
          y: 0,
        }}
        whileHover={{ scale: 1 }}
        whileTap={{ scale: 1 }}
        transition={{ duration: 0.16, ease: "easeOut" }}
      >
        <PixelChatIcon isOpen={isOpen} />
        <span className="live-chat-float-label text-base font-extrabold leading-none">
          {isOpen ? "Close" : "Support"}
        </span>
        {unread > 0 && (
          <span className="absolute -right-1 -top-1 flex h-6 min-w-6 items-center justify-center rounded-full bg-red-500 px-1 text-xs font-bold">
            {unread}
          </span>
        )}
      </motion.button>
    </div>
  );
}

export function useParticles(count = 40) {
  const [particles, setParticles] = useState([]);

  useEffect(() => {
    const generated = Array.from({ length: count }, (_, index) => ({
      id: `particle-${index}`,
      x: Math.random() * 100,
      y: Math.random() * 100,
      size: Math.random() * 4 + 2,
      duration: Math.random() * 20 + 10,
    }));

    setParticles(generated);
  }, [count]);

  return particles;
}

export function useGameData(dataFile, refreshIntervalMs = 30000) {
  const [items, setItems] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(Boolean(dataFile));

  useEffect(() => {
    if (!dataFile) {
      setItems([]);
      setError(null);
      setIsLoading(false);
      return undefined;
    }

    let isMounted = true;
    setIsLoading(true);

    const fetchItems = async () => {
      try {
        const response = await fetch(dataUrl(dataFile), { cache: "no-store" });
        if (!response.ok) {
          throw new Error(`HTTP ${response.status}`);
        }

        const data = await response.json();
        if (isMounted) {
          setItems(Array.isArray(data) ? data : []);
          setError(null);
          setIsLoading(false);
        }
      } catch (fetchError) {
        console.error(`Erreur chargement ${dataFile}:`, fetchError);
        if (isMounted) {
          setError(fetchError);
          setIsLoading(false);
        }
      }
    };

    fetchItems();
    const intervalId = setInterval(fetchItems, refreshIntervalMs);

    return () => {
      isMounted = false;
      clearInterval(intervalId);
    };
  }, [dataFile, refreshIntervalMs]);

  return { items, error, isLoading };
}

export function BackgroundParticles({ particles, animated = true }) {
  return (
    <div className="pointer-events-none absolute inset-0 overflow-hidden">
      {particles.map((particle) =>
        animated ? (
          <motion.div
            key={particle.id}
            className="absolute rounded-full bg-purple-500/20 shadow-lg"
            style={{
              top: `${particle.y}%`,
              left: `${particle.x}%`,
              width: particle.size,
              height: particle.size,
            }}
            animate={{ y: ["0%", "-200%"], opacity: [0.2, 0] }}
            transition={{ duration: particle.duration, repeat: Infinity, ease: "linear" }}
          />
        ) : (
          <div
            key={particle.id}
            className="absolute rounded-full bg-purple-500/20 shadow-lg"
            style={{
              top: `${particle.y}%`,
              left: `${particle.x}%`,
              width: particle.size,
              height: particle.size,
              animation: `float ${particle.duration}s linear infinite`,
            }}
          />
        )
      )}
    </div>
  );
}

export function ArcadeBackground() {
  const pixelRain = [
    { x: "6%", size: 3, height: 16, color: "rgba(216, 180, 254, 0.32)", delay: 0, duration: 7.6 },
    { x: "13%", size: 2, height: 10, color: "rgba(196, 181, 253, 0.24)", delay: 1.2, duration: 8.8 },
    { x: "21%", size: 3, height: 14, color: "rgba(244, 114, 182, 0.22)", delay: 2.4, duration: 7.2 },
    { x: "29%", size: 2, height: 12, color: "rgba(167, 139, 250, 0.34)", delay: 0.7, duration: 9.4 },
    { x: "37%", size: 3, height: 18, color: "rgba(221, 214, 254, 0.22)", delay: 3.1, duration: 8.1 },
    { x: "46%", size: 2, height: 11, color: "rgba(192, 132, 252, 0.3)", delay: 1.8, duration: 7.8 },
    { x: "54%", size: 3, height: 15, color: "rgba(129, 140, 248, 0.28)", delay: 0.4, duration: 8.6 },
    { x: "63%", size: 2, height: 10, color: "rgba(216, 180, 254, 0.24)", delay: 2.9, duration: 9.1 },
    { x: "71%", size: 3, height: 17, color: "rgba(244, 114, 182, 0.18)", delay: 1.1, duration: 7.4 },
    { x: "79%", size: 2, height: 13, color: "rgba(167, 139, 250, 0.32)", delay: 3.7, duration: 8.9 },
    { x: "87%", size: 3, height: 16, color: "rgba(221, 214, 254, 0.2)", delay: 2.1, duration: 7.9 },
    { x: "94%", size: 2, height: 9, color: "rgba(192, 132, 252, 0.25)", delay: 4.2, duration: 9.7 },
  ];
  const circuitLines = [
    { left: "9%", top: "18%", width: "18rem", rotate: 0, delay: 0 },
    { left: "62%", top: "22%", width: "22rem", rotate: 0, delay: 0.7 },
    { left: "14%", top: "54%", width: "15rem", rotate: 90, delay: 1.4 },
    { left: "72%", top: "58%", width: "18rem", rotate: 90, delay: 2.1 },
    { left: "30%", top: "76%", width: "24rem", rotate: 0, delay: 2.8 },
  ];
  const pixelBlocks = [
    { left: "8%", top: "35%", width: 54, height: 22, color: "rgba(168, 85, 247, 0.12)" },
    { left: "18%", top: "72%", width: 34, height: 34, color: "rgba(244, 114, 182, 0.1)" },
    { left: "39%", top: "16%", width: 46, height: 18, color: "rgba(221, 214, 254, 0.1)" },
    { left: "58%", top: "68%", width: 68, height: 26, color: "rgba(129, 140, 248, 0.12)" },
    { left: "83%", top: "38%", width: 38, height: 38, color: "rgba(192, 132, 252, 0.11)" },
  ];
  const signalTiles = [
    { left: "11%", top: "24%", size: 5, color: "rgba(250, 204, 21, 0.34)", delay: 0.2 },
    { left: "24%", top: "46%", size: 4, color: "rgba(244, 114, 182, 0.42)", delay: 1.1 },
    { left: "34%", top: "64%", size: 6, color: "rgba(216, 180, 254, 0.38)", delay: 2 },
    { left: "51%", top: "29%", size: 4, color: "rgba(134, 239, 172, 0.24)", delay: 2.7 },
    { left: "69%", top: "43%", size: 5, color: "rgba(244, 114, 182, 0.34)", delay: 3.4 },
    { left: "88%", top: "20%", size: 4, color: "rgba(216, 180, 254, 0.42)", delay: 4.2 },
  ];

  return (
    <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden bg-[#080512]">
      <div
        className="absolute inset-0"
        style={{
          background:
            "radial-gradient(ellipse at 18% 12%, rgba(124, 58, 237, 0.34), transparent 30%), radial-gradient(ellipse at 78% 18%, rgba(168, 85, 247, 0.2), transparent 26%), radial-gradient(ellipse at 55% 78%, rgba(109, 40, 217, 0.28), transparent 34%), radial-gradient(ellipse at 12% 84%, rgba(236, 72, 153, 0.12), transparent 26%), radial-gradient(ellipse at 92% 62%, rgba(129, 140, 248, 0.14), transparent 24%), linear-gradient(180deg, #170a32 0%, #0c0820 48%, #03020a 100%)",
        }}
      />
      <div
        className="absolute inset-0 opacity-[0.24]"
        style={{
          backgroundImage:
            "linear-gradient(90deg, transparent 0 23px, rgba(216, 180, 254, 0.08) 23px 24px, transparent 24px 96px), linear-gradient(180deg, transparent 0 31px, rgba(168, 85, 247, 0.08) 31px 32px, transparent 32px 128px), linear-gradient(135deg, transparent 0 46%, rgba(244, 114, 182, 0.08) 46% 47%, transparent 47% 100%)",
          backgroundSize: "96px 128px, 128px 96px, 220px 220px",
        }}
      />

      <motion.div
        className="absolute -left-1/4 top-1/4 h-[42rem] w-[54rem] opacity-[0.18] blur-3xl"
        style={{ background: "linear-gradient(120deg, rgba(168,85,247,0), rgba(168,85,247,0.38), rgba(236,72,153,0.16), rgba(99,102,241,0))", clipPath: "polygon(0 18%, 86% 0, 100% 72%, 14% 100%)" }}
        animate={{ rotate: [0, 18, 0], x: ["0%", "8%", "0%"], y: ["0%", "-5%", "0%"] }}
        transition={{ duration: 14, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        className="absolute -right-1/4 top-0 h-[36rem] w-[48rem] opacity-[0.14] blur-3xl"
        style={{ background: "linear-gradient(150deg, rgba(236,72,153,0.16), rgba(124,58,237,0.28) 42%, transparent 68%)", clipPath: "polygon(8% 0, 100% 18%, 82% 100%, 0 76%)" }}
        animate={{ scale: [1, 1.12, 1], x: ["0%", "-5%", "0%"], y: ["0%", "7%", "0%"] }}
        transition={{ duration: 11, repeat: Infinity, ease: "easeInOut" }}
      />

      <motion.div
        className="absolute inset-0 opacity-[0.16]"
        style={{
          backgroundImage:
            "linear-gradient(rgba(168, 85, 247, 0.2) 1px, transparent 1px), linear-gradient(90deg, rgba(216, 180, 254, 0.12) 1px, transparent 1px)",
          backgroundSize: "48px 48px",
        }}
        animate={{ backgroundPosition: ["0px 0px", "48px 48px"] }}
        transition={{ duration: 5.8, repeat: Infinity, ease: "linear" }}
      />

      <motion.div
        className="absolute inset-0 opacity-[0.18]"
        style={{
          backgroundImage:
            "linear-gradient(115deg, transparent 0 18%, rgba(244, 114, 182, 0.12) 18% 19%, transparent 19% 42%, rgba(216, 180, 254, 0.1) 42% 43%, transparent 43% 100%), linear-gradient(245deg, transparent 0 28%, rgba(250, 204, 21, 0.08) 28% 29%, transparent 29% 68%, rgba(134, 239, 172, 0.06) 68% 69%, transparent 69% 100%)",
          backgroundSize: "340px 340px, 460px 460px",
        }}
        animate={{ backgroundPosition: ["0px 0px, 0px 0px", "340px 170px, -230px 230px"] }}
        transition={{ duration: 18, repeat: Infinity, ease: "linear" }}
      />

      <motion.div
        className="absolute inset-0 opacity-[0.07]"
        style={{
          backgroundImage:
            "linear-gradient(transparent 0, transparent 8px, rgba(216, 180, 254, 0.72) 9px, transparent 10px)",
          backgroundSize: "100% 10px",
        }}
        animate={{ y: ["0px", "10px"] }}
        transition={{ duration: 0.7, repeat: Infinity, ease: "linear" }}
      />

      <motion.div
        className="absolute -inset-x-1/3 top-0 h-full opacity-[0.28]"
        style={{
          background:
            "linear-gradient(115deg, transparent 0%, transparent 34%, rgba(147, 51, 234, 0.18) 45%, rgba(217, 70, 239, 0.1) 51%, transparent 64%, transparent 100%)",
        }}
        animate={{ x: ["-24%", "24%", "-24%"] }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      />

      <motion.div
        className="absolute -inset-x-1/4 bottom-0 h-1/2 opacity-[0.24]"
        style={{
          background:
            "linear-gradient(8deg, rgba(124, 58, 237, 0.18), transparent 42%), linear-gradient(-8deg, rgba(221, 214, 254, 0.06), transparent 38%)",
        }}
        animate={{ y: ["8%", "-3%", "8%"] }}
        transition={{ duration: 6, repeat: Infinity, ease: "linear" }}
      />

      <motion.div
        className="absolute inset-x-0 bottom-0 h-[34vh] opacity-[0.32]"
        style={{
          background:
            "linear-gradient(180deg, transparent 0%, rgba(12, 8, 32, 0.26) 34%, rgba(3, 2, 10, 0.86) 100%), repeating-linear-gradient(90deg, transparent 0 18px, rgba(216, 180, 254, 0.1) 18px 20px, transparent 20px 44px, rgba(244, 114, 182, 0.08) 44px 48px), linear-gradient(180deg, transparent 0 42%, rgba(168, 85, 247, 0.12) 42% 44%, transparent 44% 58%, rgba(216, 180, 254, 0.08) 58% 60%, transparent 60%)",
          clipPath:
            "polygon(0 46%, 7% 46%, 7% 35%, 12% 35%, 12% 58%, 18% 58%, 18% 27%, 23% 27%, 23% 50%, 31% 50%, 31% 39%, 36% 39%, 36% 64%, 43% 64%, 43% 31%, 49% 31%, 49% 54%, 57% 54%, 57% 42%, 63% 42%, 63% 61%, 70% 61%, 70% 29%, 75% 29%, 75% 51%, 84% 51%, 84% 38%, 89% 38%, 89% 57%, 100% 57%, 100% 100%, 0 100%)",
        }}
        animate={{ opacity: [0.24, 0.38, 0.24] }}
        transition={{ duration: 5.5, repeat: Infinity, ease: "easeInOut" }}
      />

      {circuitLines.map((line) => (
        <motion.span
          key={`${line.left}-${line.top}`}
          className="absolute h-px origin-left bg-gradient-to-r from-transparent via-purple-300/40 to-transparent"
          style={{
            left: line.left,
            top: line.top,
            width: line.width,
            transform: `rotate(${line.rotate}deg)`,
          }}
          animate={{ opacity: [0.12, 0.58, 0.12], scaleX: [0.72, 1, 0.72] }}
          transition={{ duration: 4.8, delay: line.delay, repeat: Infinity, ease: "easeInOut" }}
        />
      ))}

      {pixelBlocks.map((block) => (
        <motion.span
          key={`${block.left}-${block.top}`}
          className="absolute border border-purple-300/10 shadow-[0_0_18px_rgba(168,85,247,0.14)]"
          style={{
            left: block.left,
            top: block.top,
            width: block.width,
            height: block.height,
            background: block.color,
            imageRendering: "pixelated",
          }}
          animate={{ opacity: [0.25, 0.62, 0.25] }}
          transition={{ duration: 3.6, repeat: Infinity, ease: "linear" }}
        />
      ))}

      {signalTiles.map((tile) => (
        <motion.span
          key={`${tile.left}-${tile.top}`}
          className="absolute shadow-[0_0_12px_rgba(216,180,254,0.3)]"
          style={{
            left: tile.left,
            top: tile.top,
            width: tile.size,
            height: tile.size,
            background: tile.color,
            imageRendering: "pixelated",
          }}
          animate={{ opacity: [0.15, 0.95, 0.28, 0.68, 0.15], scale: [1, 1.35, 1, 1.18, 1] }}
          transition={{ duration: 4.6, delay: tile.delay, repeat: Infinity, ease: "linear" }}
        />
      ))}

      {pixelRain.map((pixel) => (
        <motion.span
          key={`${pixel.x}-${pixel.delay}`}
          className="absolute top-[-12vh] shadow-[0_0_14px_rgba(168,85,247,0.42)]"
          style={{
            left: pixel.x,
            width: pixel.size,
            height: pixel.height,
            background: pixel.color,
            imageRendering: "pixelated",
          }}
          animate={{ opacity: [0, 0.75, 0.18, 0], y: ["-12vh", "112vh"] }}
          transition={{ duration: pixel.duration, delay: pixel.delay, repeat: Infinity, ease: "linear" }}
        />
      ))}
    </div>
  );
}

export const ARCADE_SECTION_CLASS =
  "bg-purple-950/25 backdrop-blur-[2px] border-b border-purple-400/20 rounded-b-3xl shadow-[0_18px_60px_rgba(124,58,237,0.2)]";

export const ARCADE_CARD_CLASS =
  "bg-gray-950/55 border border-purple-400/20 shadow-[0_16px_45px_rgba(124,58,237,0.18)]";

export function GlobalProductSearch({ onNavigate }) {
  const [query, setQuery] = useState("");
  const [products, setProducts] = useState([]);
  const normalizedQuery = query.trim().toLowerCase();

  useEffect(() => {
    let active = true;

    Promise.all(
      ACTIVE_GAME_CATALOG.map(async (game) => {
        try {
          const response = await fetch(dataUrl(game.dataFile), { cache: "no-store" });
          if (!response.ok) return [];

          const rows = await response.json();
          if (!Array.isArray(rows)) return [];

          return rows.map((item) => ({ ...item, gameName: game.name, gameImage: game.image }));
        } catch {
          return [];
        }
      })
    ).then((groups) => {
      if (active) setProducts(groups.flat());
    });

    return () => {
      active = false;
    };
  }, []);

  const results = useMemo(() => {
    if (normalizedQuery.length < 2) return [];

    return products
      .filter((item) => `${item.name || ""} ${item.gameName || ""}`.toLowerCase().includes(normalizedQuery))
      .slice(0, 6);
  }, [normalizedQuery, products]);

  const closeSearch = () => {
    setQuery("");
    onNavigate?.();
  };

  return (
    <div className="relative w-full md:w-72 lg:w-80">
      <Search className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-purple-200/70" size={18} />
      <input
        type="search"
        value={query}
        onChange={(event) => setQuery(event.target.value)}
        placeholder="Search products..."
        className="w-full rounded-lg border border-purple-400/20 bg-black/30 py-2 pl-10 pr-3 text-base font-bold text-white placeholder:text-gray-400 focus:border-purple-300 focus:outline-none focus:ring-2 focus:ring-purple-500/50"
      />

      {normalizedQuery.length >= 2 && (
        <div className="absolute left-0 right-0 top-[calc(100%+0.45rem)] z-[70] overflow-hidden rounded-lg border border-purple-400/25 bg-[#020617] shadow-2xl shadow-black/70">
          {results.length === 0 ? (
            <p className="px-3 py-3 text-base font-bold text-gray-400">No product found</p>
          ) : (
            results.map((item) => (
              <Link
                key={`${item.gameName}-${item.name}-${item.link}`}
                to={item.link}
                onClick={closeSearch}
                className="flex items-center gap-3 border-b border-purple-400/10 px-3 py-2 last:border-b-0 hover:bg-purple-500/15"
              >
                <FallbackImage
                  src={item.logo || item.gameImage}
                  alt={item.name}
                  label={item.name}
                  className="h-10 w-10 shrink-0 rounded-md border border-purple-400/20 bg-gray-900 object-cover"
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-base font-extrabold text-purple-100">{item.name}</span>
                  <span className={`block truncate text-sm font-bold ${STATUS_COLORS[item.status] || "text-gray-300"}`}>
                    {item.gameName} - {statusLabel(item.status)}
                  </span>
                </span>
              </Link>
            ))
          )}
        </div>
      )}
    </div>
  );
}

const DISCORD_INVITE_URL = "https://discord.gg/HdZBtMFsWA";

function DiscordLaunchOverlay({ active }) {
  if (!active) return null;

  return (
    <div className="discord-launch-overlay" aria-hidden="true">
      <span className="discord-terminal-backdrop" />
      <div className="discord-launch-stage">
        <span className="discord-terminal-particle discord-terminal-particle-one" />
        <span className="discord-terminal-particle discord-terminal-particle-two" />
        <span className="discord-terminal-particle discord-terminal-particle-three" />
        <span className="discord-terminal-particle discord-terminal-particle-four" />
        <span className="discord-terminal-particle discord-terminal-particle-five" />

        <div className="discord-terminal-portal">
          <span className="discord-terminal-ring discord-terminal-ring-one" />
          <span className="discord-terminal-ring discord-terminal-ring-two" />
          <span className="discord-terminal-core">D</span>
          <span className="discord-terminal-beam" />
        </div>

        <div className="discord-terminal-card">
          <div className="discord-terminal-header">
            <div className="discord-terminal-lights">
              <span />
              <span />
              <span />
            </div>
            <p>SUPPORT TERMINAL</p>
            <strong>LIVE</strong>
          </div>
          <div className="discord-terminal-body">
            <div className="discord-terminal-command-panel">
              <p className="discord-terminal-line">
                <span className="discord-terminal-user">root@hackseller</span>
                <span className="discord-terminal-path">:/help$</span>
                <span className="discord-terminal-command">open discord.gg/HdZBtMFsWA</span>
                <span className="discord-terminal-cursor" />
                <span className="discord-terminal-enter">ENTER</span>
              </p>
              <div className="discord-terminal-sequence">
                <p className="discord-terminal-status">resolving support gateway</p>
                <p className="discord-terminal-status">validating invite token</p>
                <p className="discord-terminal-status discord-terminal-status-ready">redirect accepted</p>
              </div>
            </div>
            <div className="discord-terminal-route-panel">
              <div className="discord-terminal-route-map">
                <span className="discord-terminal-route-node discord-terminal-route-node-home">HS</span>
                <span className="discord-terminal-route-line" />
                <span className="discord-terminal-route-packet" />
                <span className="discord-terminal-route-node discord-terminal-route-node-discord">D</span>
              </div>
              <div className="discord-terminal-meta">
                <span>DISCORD</span>
                <span>SSL OK</span>
                <span>302 READY</span>
              </div>
            </div>
            <span className="discord-terminal-loader" />
          </div>
        </div>
      </div>
    </div>
  );
}

function NavLinks({ onNavigate, onHelpClick }) {
  const linkClass = ({ isActive }) =>
    `site-nav-link ${isActive ? "site-nav-link-active" : ""}`;

  return (
    <>
      <li>
        <a
          href={DISCORD_INVITE_URL}
          className="site-nav-link"
          onClick={onHelpClick}
        >
          Help
        </a>
      </li>
      <li>
        <NavLink to="/Status" className={linkClass} onClick={onNavigate}>
          Status
        </NavLink>
      </li>
      <li>
        <NavLink to="/Spoofer" className={linkClass} onClick={onNavigate}>
          Spoofer
        </NavLink>
      </li>
      <li>
        <NavLink to="/Dma" className={linkClass} onClick={onNavigate}>
          DMA
        </NavLink>
      </li>
      <li>
        <NavLink to="/faq" className={linkClass} onClick={onNavigate}>
          FAQ
        </NavLink>
      </li>
      <li>
        <NavLink to="/Manual" className={linkClass} onClick={onNavigate}>
          Manual
        </NavLink>
      </li>
    </>
  );
}

function SystemBroadcastBar() {
  const [supportStatus, setSupportStatus] = useState(getLisbonSupportStatus);

  useEffect(() => {
    const intervalId = setInterval(() => setSupportStatus(getLisbonSupportStatus()), 30000);
    return () => clearInterval(intervalId);
  }, []);

  return (
    <div className="system-broadcast-bar" aria-label="Site system status">
      <span className="system-broadcast-led" />
      <span>LIVE CATALOG</span>
      <span className="system-broadcast-separator" />
      <span>{supportStatus.schedule}</span>
      <span className="system-broadcast-separator" />
      <span>{supportStatus.state}</span>
      <span className="system-broadcast-separator" />
      <span>DISCORD GATEWAY READY</span>
    </div>
  );
}

export function SiteNavbar({ global = false } = {}) {
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isDiscordLaunching, setIsDiscordLaunching] = useState(false);
  const [discordLaunchId, setDiscordLaunchId] = useState(0);
  const discordLaunchTimeoutRef = useRef(null);
  const isPreview = new URLSearchParams(location.search).get("preview") === "1";

  useEffect(() => {
    return () => {
      if (discordLaunchTimeoutRef.current) {
        clearTimeout(discordLaunchTimeoutRef.current);
      }
    };
  }, []);

  if (!global || location.pathname === "/ops-vault-a91f6d" || isPreview) {
    return null;
  }

  const handleHelpClick = (event) => {
    event.preventDefault();
    setIsMenuOpen(false);
    setIsDiscordLaunching(true);
    setDiscordLaunchId((current) => current + 1);

    if (discordLaunchTimeoutRef.current) {
      clearTimeout(discordLaunchTimeoutRef.current);
    }

    discordLaunchTimeoutRef.current = setTimeout(() => {
      window.location.assign(DISCORD_INVITE_URL);
    }, 2400);
  };

  return (
    <nav
      className="site-navbar font-jersey bg-gray-950/55 backdrop-blur-xl shadow-[0_12px_45px_rgba(124,58,237,0.24)] px-4 md:px-8 py-4 flex justify-between items-center fixed top-0 w-full z-50 rounded-b-xl border border-purple-400/20"
    >
      <h1 className="text-3xl md:text-4xl font-extrabold text-purple-300 tracking-tight drop-shadow-glow cursor-pointer">
        <Link to="/" className="hover:text-purple-200 transition-colors">
          Hack<span className="text-white">seller</span>
        </Link>
      </h1>

      <button
        type="button"
        onClick={() => setIsMenuOpen((prev) => !prev)}
        className="relative z-[75] md:hidden text-gray-200 hover:text-purple-300 transition-colors"
        aria-label="Toggle menu"
      >
        {isMenuOpen ? <X size={28} /> : <Menu size={28} />}
      </button>

      <ul className="hidden md:flex space-x-6 lg:space-x-8 text-gray-200 font-bold text-xl lg:text-2xl">
        <NavLinks onHelpClick={handleHelpClick} />
      </ul>

      <SystemBroadcastBar />

      {isMenuOpen && (
        <div className="mobile-nav-panel fixed left-0 right-0 top-[6.75rem] z-[90] max-h-[calc(100vh-6.75rem)] w-screen overflow-y-auto md:hidden">
          <div className="px-5 pb-6 pt-5">
            <div className="mobile-nav-search">
              <GlobalProductSearch onNavigate={() => setIsMenuOpen(false)} />
            </div>

            <div className="mt-5 grid grid-cols-2 gap-3">
              <Link to="/Status" onClick={() => setIsMenuOpen(false)} className="mobile-nav-tile">
                <Activity size={18} />
                <span>Status</span>
                <strong>Live check</strong>
              </Link>
              <a href={DISCORD_INVITE_URL} onClick={handleHelpClick} className="mobile-nav-tile">
                <ShieldCheck size={18} />
                <span>Support</span>
                <strong>Discord</strong>
              </a>
            </div>

            <div className="mt-5 rounded-lg border border-purple-300/15 bg-white/[0.03] p-4">
              <p className="mb-3 text-sm font-extrabold uppercase text-purple-200/80">Navigation</p>
              <ul className="grid gap-3 text-gray-200 text-xl font-bold">
                <NavLinks onNavigate={() => setIsMenuOpen(false)} onHelpClick={handleHelpClick} />
              </ul>
            </div>

            <div className="mt-4 grid grid-cols-2 gap-3 text-lg font-bold">
              <Link to="/Delivery" onClick={() => setIsMenuOpen(false)} className="mobile-nav-secondary">
                Delivery
              </Link>
              <Link to="/Blog" onClick={() => setIsMenuOpen(false)} className="mobile-nav-secondary">
                Blog
              </Link>
              <Link to="/Manual" onClick={() => setIsMenuOpen(false)} className="mobile-nav-secondary">
                Manual
              </Link>
            </div>
          </div>
        </div>
      )}

      <DiscordLaunchOverlay key={discordLaunchId} active={isDiscordLaunching} />
    </nav>
  );
}

export function SiteFooter() {
  const currentYear = new Date().getFullYear();

  return (
    <footer className="site-footer footer-command font-jersey border-t border-purple-400/20 bg-gray-950/86 px-4 py-5 text-white backdrop-blur-xl md:py-6">
      <div className="relative z-10 mx-auto w-full max-w-7xl">
        <div className="grid gap-5 md:grid-cols-[1.25fr_0.8fr_1fr] md:items-start">
          <div className="min-w-0">
            <p className="text-3xl font-extrabold tracking-normal text-purple-300 drop-shadow-glow md:text-4xl">
              Hack<span className="text-white">Seller</span>
            </p>
            <p className="mt-3 text-sm font-bold text-gray-500">
              © 2024-{currentYear} HackSeller. All rights reserved.
            </p>
          </div>

          <div className="footer-link-block">
            <h3 className="mb-2 text-sm font-extrabold uppercase text-purple-300">Shop</h3>
            <div className="grid gap-1.5 text-base font-bold text-gray-300">
              <Link to="/" className="footer-link">
                Home
              </Link>
              <Link to="/Manual" className="footer-link">
                Manual
              </Link>
              <Link to="/Blog" className="footer-link">
                Blog
              </Link>
            </div>
          </div>

          <div className="footer-link-block">
            <h3 className="mb-2 text-sm font-extrabold uppercase text-purple-300">Information</h3>
            <div className="grid gap-1.5 text-base font-bold text-gray-300">
              <Link to="/Termsofuse" className="footer-link">
                Terms of use
              </Link>
              <Link to="/Privacy" className="footer-link">
                Privacy Policy
              </Link>
              <Link to="/Delivery" className="footer-link">
                Delivery, payment, returns
              </Link>
              <a href="mailto:kekra@hackseller.store" className="footer-link">
                Cooperation and contacts
              </a>
            </div>
          </div>
        </div>

        <div className="footer-support-line mt-5 flex flex-wrap items-center gap-3 border-t border-purple-300/10 pt-3 text-xs font-bold uppercase text-gray-400">
          <span className="inline-flex items-center gap-2 text-green-200/80">
            <Activity size={15} />
            Support 09:00-23:00 Lisbon
          </span>
          <span className="inline-flex items-center gap-2">
            <ShieldCheck size={15} className="text-purple-300" />
            Live product status
          </span>
          <a href="mailto:kekra@hackseller.store" className="footer-link inline-flex items-center gap-2">
            <Send size={15} className="text-purple-300" />
            Contact
          </a>
        </div>
      </div>
    </footer>
  );
}
